import React, {useEffect, useRef, useState} from 'react'
import {DvViewItemType, useAppDispatch, type PocketItem} from '@dv5/domain'
import {pocketService} from '../services/pocketService'
import {PocketListItem} from './PocketListItem'
import {AddItemDialog} from './AddItemDialog'
import './PocketList.less'

interface PocketListProps {
    items: Array<PocketItem>
    selectedIndex: number | undefined
    onClose: () => void
}

export const PocketList: React.FC<PocketListProps> = ({
                                                          items,
                                                          selectedIndex,
                                                          onClose,
                                                      }) => {
    const dispatch = useAppDispatch()
    const modalRef = useRef<HTMLDivElement>(null)
    const itemCount = items.length
    const title = pocketService.getTooltipText(itemCount)
    const hasItems = itemCount > 0
    const [showAddDialog, setShowAddDialog] = useState(false)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showAddDialog) {
                return
            }
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [onClose, showAddDialog])

    const handleClear = () => {
        pocketService.clear(dispatch)
    }

    const handleAddItem = (itemTitle: string, itemType: DvViewItemType) => {
        pocketService.addManualItem(itemTitle, itemType, dispatch)
        setShowAddDialog(false)
    }

    const handleCancelAdd = () => {
        setShowAddDialog(false)
    }

    const selectItem = (newIndex: number) => {
        pocketService.setSelectedIndex(newIndex, dispatch)
    }

    return (
        <div className="pocket-list-overlay">
            <div className="pocket-list-modal" ref={modalRef}>
                <div className="pocket-list-header">
                    <span className="pocket-list-title">{title}</span>
                    <button
                        className="pocket-list-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>
                <div className="pocket-list-content">
                    {items.length === 0 ? (
                        <div className="pocket-list-empty">No items</div>
                    ) : (
                        <ul className="pocket-list">
                            {items.map((item, index) => (
                                <PocketListItem key={item.id}
                                                item={item}
                                                index={index}
                                                active={index === selectedIndex}
                                                onSelect={selectItem}
                                />
                            ))}
                        </ul>
                    )}
                </div>
                {hasItems && (
                    <div className="pocket-list-footer">
                        <button
                            className="pocket-list-add-btn"
                            onClick={() => setShowAddDialog(true)}
                        >
                            Add
                        </button>
                        <button
                            className="pocket-list-clear-btn"
                            onClick={handleClear}
                        >
                            Clear
                        </button>
                    </div>
                )}
                {!hasItems && (
                    <div className="pocket-list-footer">
                        <button
                            className="pocket-list-add-btn"
                            onClick={() => setShowAddDialog(true)}
                        >
                            Add
                        </button>
                    </div>
                )}
            </div>
            {showAddDialog && (
                <AddItemDialog
                    onAdd={handleAddItem}
                    onCancel={handleCancelAdd}
                />
            )}
        </div>
    )
}
